import React, { useState } from 'react';
import { plural, t } from '@lingui/macro';
import { Location } from 'history';

import Avatar, { AvatarType, Size } from '@rover/components/src/Avatar/Avatar';
import CalloutBadge from '@rover/components/src/callouts/CalloutBadge/CalloutBadge';
import Modal from '@rover/components/src/OverflowTextModal/Modal';
import SimplifiedRatingsReview from '@rover/components/src/RatingsReview/SimplifiedRatingsReview';
import SitterHighlight from '@rover/components/src/SitterHighlight/SitterHighlight';
import Rebook from '@rover/icons/svg/rebook.svg';
import { Box, Button, Display, Flex, Heading, Paragraph, Text } from '@rover/kibble/core';
import { SpacingSystemType } from '@rover/kibble/core/styledSystemPropTypes';
import StarRating from '@rover/kibble/patterns/StarRating';
import useTheme from '@rover/kibble/themes/useTheme';
import type { EnhancedPersonSitterProfile } from '@rover/rsdk/src/apiClient/latest/model/enhancedPersonSitterProfile';
import { useCurrentUser } from '@rover/rsdk/src/modules/Data/Core/CurrentUser';

import { highlightNumberInText } from '../../../../search/SearchPage/utilities/searchCardTreatment';
import FavoriteButton from '../../../components/FavoriteButton';
import { getLocationString } from '../../utils/utils';
import { LINE_CLAMP_2_STYLES } from '../RevampProfileHeader/profileHeaderUtils';

type SitterProps = {
  sitter: EnhancedPersonSitterProfile;
  sitterImage?: string;
  variant?: 'compact' | 'full';
  justifyContent?: string;
  ownProfile?: boolean;
  isApproved?: boolean;
  isInLaunchedCountry?: boolean;
  onAvatarClick?: (index: number) => void;
  isUnderReview?: boolean;
  onError?: (string) => void;
  location?: Location;
  enableRichResults?: boolean;
  shouldShowRevampLayout?: boolean;
  onReviewsCountPress?: () => void;
};

const BadgeIcon = (icon: string, title: string): JSX.Element => (
  <img src={icon} alt={title} width="100%" />
);

const SitterInformation = ({
  sitter,
  sitterImage,
  variant = 'full',
  justifyContent = 'center',
  ownProfile,
  isApproved = true,
  isInLaunchedCountry,
  onAvatarClick,
  isUnderReview,
  onError,
  location,
  enableRichResults,
  shouldShowRevampLayout = false,
  onReviewsCountPress,
}: SitterProps): JSX.Element => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const theme = useTheme();
  const currentUser = useCurrentUser();

  const handleOpenModal = ({ webEvent }: { webEvent?: React.MouseEvent<HTMLElement> }): void => {
    webEvent?.stopPropagation();
    setIsModalOpen(true);
  };

  const handleCloseModal = (): void => {
    setIsModalOpen(false);
  };

  const BADGE_SIZE = variant === 'compact' ? '24px' : ['24px', '32px'];
  const badge = sitter.badges[0];

  const renderAvatar = (size: string): JSX.Element => (
    <Box
      position="relative"
      width={size}
      mr={shouldShowRevampLayout ? '4x' : '8x'}
      sx={{ cursor: onAvatarClick ? 'pointer' : 'default' }}
      onClick={onAvatarClick ? () => onAvatarClick(0) : undefined}
    >
      <Avatar
        diameter={size}
        size={Size.Profile}
        borderRadius={shouldShowRevampLayout ? '8px' : '50%'}
        avatarType={AvatarType.Person}
        imageUrl={sitterImage || sitter.defaultImage.medium}
      />
      {badge && !shouldShowRevampLayout && (
        <Flex
          alignItems="bottom"
          position="absolute"
          width={BADGE_SIZE}
          height={BADGE_SIZE}
          right="0"
          bottom="0"
        >
          {variant === 'full' ? (
            <Button
              icon={() => BadgeIcon(badge.icon, badge.title)}
              circular
              aria-label={badge.title}
              onPress={handleOpenModal}
              m="0x"
              p="0x"
              sx={{
                backgroundColor: 'transparent',
                '&:hover': { background: 'none' },
                '&:focus': { background: 'none' },
                border: 0,
              }}
              data-testid="badge-icon-button"
            />
          ) : (
            BadgeIcon(badge.icon, badge.title)
          )}
        </Flex>
      )}
    </Box>
  );

  const styles = {
    compact: {
      fontSize: theme.display[400].fontSize,
      avatarDiameter: '80px',
      direction: 'row',
      alignItems: 'center',
      minHeight: '0px',
      badgeDirection: ['initial', 'column', 'row'],
      badgeAlignment: ['initial', 'start', 'center'],
      sitterNameMt: '0x' as SpacingSystemType,
      badgeSpacing: {
        pl: ['0x', '0x', '2x'],
        mt: ['1x', '2x', '1x'],
      },
      mb: undefined,
    },
    full: {
      fontSize: theme.display[500].fontSize,
      avatarDiameter: '112px',
      direction: 'column',
      alignItems: 'start',
      minHeight: '24px',
      badgeDirection: 'column',
      badgeAlignment: 'initial',
      sitterNameMt: '1x' as SpacingSystemType,
      badgeSpacing: {
        pl: undefined,
        mt: '1x',
      },
      mb: '8x',
    },
  };

  const calloutBadge = (
    <Box sx={{ ...styles[variant].badgeSpacing }}>
      {isApproved && sitter.providerProfile?.isActiveStarSitter && (
        <CalloutBadge value="Star Sitter" calloutBadgeStyle={CalloutBadge.Styles.STAR_SITTER} />
      )}
      {!isApproved && (
        <CalloutBadge value={t`Coming Soon`} calloutBadgeStyle={CalloutBadge.Styles.COMING_SOON} />
      )}
    </Box>
  );

  const repeatCount = sitter.providerProfile?.repeatClientCount || 0;

  if (shouldShowRevampLayout) {
    return (
      <Flex flexDirection="row" alignItems="center" data-testid="sitter-information" flex="1 0 0">
        <Flex flexShrink="0">{renderAvatar('100px')}</Flex>
        <Flex flexShrink="1" flexGrow="1" minWidth="0" alignItems="center">
          <Box flexShrink="1" minWidth="0">
            <Display mb="0x" size="400" sx={LINE_CLAMP_2_STYLES}>
              {sitter.shortName}
            </Display>
          </Box>
          <Flex ml="4x" flexShrink="0">
            <SimplifiedRatingsReview
              ratingsAverage={sitter.ratingsAverage}
              reviewsCount={sitter.reviewsCount}
              onReviewsCountPress={onReviewsCountPress}
            />
          </Flex>
          {repeatCount > 0 && (
            <Flex ml="4x" flexShrink="0">
              <SitterHighlight
                dataTestId="repeat-clients-highlight"
                text={highlightNumberInText(repeatCount.toString())}
                icon={Rebook}
                ariaLabel={plural(repeatCount, {
                  one: '# repeat client',
                  other: '# repeat clients',
                })}
              />
            </Flex>
          )}
        </Flex>
      </Flex>
    );
  }

  return (
    <>
      <Flex
        flexDirection={styles[variant].direction}
        justifyContent={justifyContent}
        alignItems={styles[variant].alignItems}
        width="100%"
        data-testid="sitter-information"
        sx={{ mb: styles[variant].mb }}
      >
        <Box display={['block', 'none']}>{renderAvatar('64px')}</Box>
        <Box display={['none', 'block']}>{renderAvatar(styles[variant].avatarDiameter)}</Box>
        <Box width="100%">
          <Flex
            flexDirection={styles[variant].badgeDirection}
            alignItems={styles[variant].badgeAlignment}
            width="100%"
          >
            <Flex width="100%" justifyContent="space-between">
              <Flex>
                <Display mt={styles[variant].sitterNameMt} mb="0x">
                  <Box
                    pr="3x"
                    sx={{ fontSize: styles[variant].fontSize }}
                    webProps={enableRichResults ? { itemProp: 'name' } : undefined}
                  >
                    {sitter.shortName}
                  </Box>
                </Display>
                {variant === 'compact' && calloutBadge}
              </Flex>
              {!ownProfile &&
                isApproved &&
                sitter.authenticatedContext?.canFavorite !== false &&
                location && (
                  <Flex flexDirection="column" alignItems="end">
                    <FavoriteButton
                      sitterInfo={sitter}
                      isLoggedIn={currentUser.isAuthenticated}
                      onError={onError}
                      location={location}
                    />
                  </Flex>
                )}
            </Flex>
          </Flex>
          {variant === 'full' && calloutBadge}
          <Paragraph
            size="200"
            mt="1x"
            mb="1x"
            webProps={enableRichResults ? { itemProp: 'areaServed' } : undefined}
          >
            {getLocationString(sitter.services[0]?.meta)}
          </Paragraph>
          {sitter.reviewsCount > 0 && (
            <Flex alignItems="center" minHeight={styles[variant].minHeight}>
              <>
                {parseFloat(sitter.ratingsAverage) > 0 && (
                  <>
                    <StarRating
                      rating={parseFloat(sitter.ratingsAverage)}
                      variant="compact"
                      data-testid="star-rating"
                      reviewCount={enableRichResults ? sitter.reviewsCount : undefined}
                      itemProp={enableRichResults ? 'aggregateRating' : undefined}
                    />
                    <Text px="2x">•</Text>
                  </>
                )}
                <Text>
                  {plural(sitter.reviewsCount, {
                    one: '# review',
                    other: '# reviews',
                  })}
                </Text>
              </>
            </Flex>
          )}
          {(!isApproved || isUnderReview) && !ownProfile && variant === 'full' && (
            <Paragraph size="200" pt="4x">
              {t`This profile has not been completed and reviewed by Rover's sitter quality team. Once it has been approved, pet parents will be able to contact ${sitter.shortName}`}
            </Paragraph>
          )}
          {(!isApproved || isUnderReview) && ownProfile && variant === 'full' && (
            <Paragraph size="200" pt="4x">
              {t`Your profile has not been completed and reviewed by Rover's sitter quality team. Once it has been approved, dog owners will be able to contact you.`}
            </Paragraph>
          )}
          {isApproved && !isUnderReview && ownProfile && variant === 'full' && (
            <Paragraph size="200" pt="4x">
              {isInLaunchedCountry
                ? t`Your profile has been approved. Pet parents viewing your profile can now send you booking requests.`
                : t`Your profile has been approved.`}
            </Paragraph>
          )}
        </Box>
      </Flex>
      {badge && (
        <Modal isOpen={isModalOpen} onClose={handleCloseModal}>
          <Box width="100%" data-testid="sitter-badge-modal">
            <Heading size="300">{badge.title}</Heading>
            {/* @ts-expect-error */}
            <Paragraph size="200" mb="8x" dangerouslySetInnerHTML={{ __html: badge.description }} />
          </Box>
        </Modal>
      )}
    </>
  );
};

export default SitterInformation;
